import { HTMLAttributes, useState } from 'react'

import Container from '@/components/base/Container'
import Motion from '@/components/base/Motion'
import SVGIcon from '@/components/base/SVGIcon'
import Typography from '@/components/base/Typography'

import { opacityReveal } from './animations/opacity'
import { cn } from '@/lib/utils'

export type AccordionProps = HTMLAttributes<HTMLElement> &
  Partial<{
    items: {
      title: string
      description: string
    }[]
  }>

export type AccordionItemProps = {
  item: {
    title: string
    description: string
  }
  isOpen: boolean
  onClick: () => void
}

const AccordionItem = ({ item, isOpen, onClick }: AccordionItemProps) => {
  return (
    <Motion animation={opacityReveal}>
      <div className="bg-[#F0F2F5] transition-colors duration-300 ease-out-cubic hover:bg-[#e5e6e9] focus:bg-[#e5e6e9]">
        <button
          onClick={onClick}
          className="flex w-full cursor-pointer items-start justify-between p-[30px] text-left"
        >
          <div className="flex">
            <Typography variant="h5">{`<b>${item.title}</b>`}</Typography>
          </div>
          <div
            className={cn(
              'ml-[10px] flex size-[12px] flex-[0_0_12px] origin-center translate-y-[10px] items-center justify-center transition-transform lg:translate-y-[14px]',
              {
                'rotate-180': !isOpen
              }
            )}
          >
            <SVGIcon symbol="arrow-down-icon" className="fill-current" />
          </div>
        </button>
        <div
          className={cn('overflow-hiddentransition-all grid duration-300', {
            'grid-rows-[1fr]': isOpen,
            'grid-rows-[0fr]': !isOpen
          })}
        >
          <div className="overflow-hidden">
            <Typography variant="body" className="block px-[30px] pb-[30px]">
              {item.description}
            </Typography>
          </div>
        </div>
      </div>
    </Motion>
  )
}

const Accordion = ({ items }: AccordionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return items?.length ? (
    <section data-accordion>
      <Container>
        <div className="space-y-[30px]">
          {items?.map((item, index) => (
            <AccordionItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              onClick={() => toggleItem(index)}
            />
          ))}
        </div>
      </Container>
    </section>
  ) : null
}

export default Accordion
